import { useEffect, useReducer, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { DURATION, EASE_OUT_SOFT, useReducedMotion } from "../lib/motion";

/**
 * Beyond Sure — PhoneShowcase (v3.2, multi-context).
 *
 * Replaces the v3 static "PhonePreviewCard". A glassmorphic, auto-cycling
 * AND tappable showcase that demonstrates BeyondSure's real capabilities.
 *
 * v3.2 widens its deployment context: it now renders in THREE places via
 * the AuthShell `showcase` slot —
 *   • phone     ≤640px  — above the form (full editorial column width)
 *   • tablet    641-1023px — above the form (same single-column treatment)
 *   • desktop   ≥1024px — integrated into the LEFT brand panel as the
 *                          hero visual, sitting beneath the headline
 *
 * Layout is fluid: the root takes its width from the parent. To prevent
 * over-stretching on a very wide LEFT panel the AuthShell caps the slot
 * at max-w-[480px]. The scene viewport's min-h scales subtly with breakpoint
 * so wide layouts don't read as squat.
 *
 * Six scenes, each with its own micro-animation:
 *   1. Chat        — user bubble + typing → streaming reply
 *   2. OCR         — policy doc with scan-line → extracted fields populate
 *   3. Compare     — 6 insurer bars (kept from v3, polished in glass)
 *   4. Truth audit — fact-check rows with ✓ / ⚠
 *   5. Renewals    — "TATA AIG renews in 12 days · ₹14,200" with pulse
 *   6. Best        — "Best for you: HDFC Optima ₹X/yr · why →" highlight
 *
 * Controls:
 *   • Auto-advances every 4.2s (token: --duration-showcase-tick).
 *   • Tap a dot to jump to that scene; auto-advance pauses for 8s after a
 *     manual jump so the user can read.
 *   • Swipe left/right on the showcase body to navigate.
 *   • All motion respects prefers-reduced-motion (no auto-advance, no
 *     intra-scene micro-anims — content renders in its final state).
 *
 * Typography:
 *   .bsure-ui (Geist) everywhere — this is functional/UI text per the brief.
 *
 * Accessibility:
 *   role="region" aria-roledescription="showcase" so screen readers describe
 *   it as a carousel. Each scene gets a tabbable indicator. Live region
 *   announces scene changes via aria-live="polite".
 */
type SceneKey = "chat" | "ocr" | "compare" | "audit" | "renewal" | "best";

interface Scene {
  key: SceneKey;
  label: string;       // shown beneath title
  title: string;       // small in-card label
  Component: React.FC;
}

const SCENES: Scene[] = [
  { key: "chat",    label: "Chat",        title: "Asking BeyondSure",     Component: ChatScene    },
  { key: "ocr",     label: "OCR",         title: "Reading your policy",   Component: OcrScene     },
  { key: "compare", label: "Compare",     title: "Comparing 6 insurers",  Component: CompareScene },
  { key: "audit",   label: "Truth audit", title: "Verifying every claim", Component: AuditScene   },
  { key: "renewal", label: "Renewals",    title: "Never miss a renewal",  Component: RenewalScene },
  { key: "best",    label: "Best plan",   title: "Best for you",          Component: BestScene    },
];

const TICK_MS = 4200;
const MANUAL_PAUSE_MS = 8000;

export function PhoneShowcase() {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [, forceTick] = useReducer((n: number) => n + 1, 0);
  const manualPauseUntil = useRef<number>(0);
  const touchStartX = useRef<number | null>(null);

  // Auto-advance, paused when the user manually navigates.
  useEffect(() => {
    if (reduced) return;
    const id = window.setInterval(() => {
      if (Date.now() < manualPauseUntil.current) return;
      setIndex((i) => (i + 1) % SCENES.length);
      forceTick();
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [reduced]);

  function jump(i: number) {
    setIndex(i);
    manualPauseUntil.current = Date.now() + MANUAL_PAUSE_MS;
  }

  // Swipe handlers — only horizontal, threshold ~50px.
  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 50) return;
    if (dx < 0) jump((index + 1) % SCENES.length);
    else jump((index - 1 + SCENES.length) % SCENES.length);
  }

  const scene = SCENES[index];
  const Active = scene.Component;

  return (
    <div
      role="region"
      aria-roledescription="showcase"
      aria-label="Compare 6 insurers in about two minutes — interactive preview"
      className={[
        "bsure-ui bsure-glass",
        "rounded-[var(--radius-xl)]",
        "px-5 py-5 sm:px-6 sm:py-6",
      ].join(" ")}
    >
      {/* Header row — v3.5 fix: the showcase scene title and PREVIEW eyebrow
          were inheriting the v5 bold display style (Hanken Grotesk weight 800)
          intended for the MAIN page headline. We override them inline here
          back to their pre-v5 look so the small-card heading reads quiet and
          editorial (Vercetti Regular for the title; the variable Hanken cut
          with the width-stretch axis intact for the eyebrow). The main page
          headline + "Welcome." form-title continue to pull bold Hanken
          Grotesk via the global .bsure-display class — untouched. */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className="bsure-caps text-[10.5px] text-[var(--color-ink-60)]"
            style={{
              // Lead with the variable woff2 cut so font-stretch: 125% +
              // font-variation-settings wght 600 from .bsure-caps actually
              // resolve to the same wider/lighter weight as v3.3 had,
              // instead of the Google-Fonts static "Hanken Grotesk" face
              // (no width axis) that --font-caps now leads with.
              fontFamily: '"Hanken Grotesk Variable", "Inter Tight", system-ui, sans-serif',
            }}
          >
            Preview
          </div>
          <div
            className="mt-1 text-[18px] leading-[1.15] text-[var(--color-ink)]"
            aria-live="polite"
            style={{
              // Vercetti Regular at 400 — the small-card heading the
              // showcase shipped with in v3.3. Do NOT use .bsure-display,
              // which is now reserved for the main page headline at
              // weight 800.
              fontFamily: 'Vercetti, "Fraunces", "Source Serif 4", Georgia, serif',
              fontWeight: 400,
              letterSpacing: "-0.01em",
              fontFeatureSettings: '"ss01", "liga"',
            }}
          >
            {scene.title}
          </div>
        </div>
        <span
          className={[
            "shrink-0 inline-flex items-center gap-1.5 rounded-[var(--radius-pill)]",
            "px-2.5 py-1",
            "bsure-caps text-[9.5px] leading-none text-[var(--color-brand-deep)]",
          ].join(" ")}
          style={{ backgroundColor: "var(--color-brand-soft)" }}
        >
          <span
            aria-hidden="true"
            className="bsure-pulse inline-block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: "var(--color-brand-vivid)" }}
          />
          Live
        </span>
      </div>

      {/* Scene viewport — fixed min-height so the card doesn't jump as
          scenes change. Swipe-aware via touch handlers. min-h scales up at
          sm+ so the showcase doesn't read squat at desktop widths.
          v3.3 fix: AnimatePresence mode="sync" (was "wait") so the new
          scene fades IN at the same time the old fades OUT — a true
          crossfade with no opacity-0 gap. The previous mode="wait" left
          a 200-480ms window where the body was effectively invisible,
          which Shivansh observed as "scene renders only title + LIVE
          badge with no body content". The fix preserves the entrance
          choreography but removes the blank moment. */}
      <div
        className="relative mt-4 min-h-[210px] sm:min-h-[232px]"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <AnimatePresence mode="sync" initial={false}>
          <motion.div
            key={scene.key}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: DURATION.fast, ease: EASE_OUT_SOFT }}
            className="absolute inset-0"
          >
            <Active />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dot indicator — 6 dots, the active one wider + brand-vivid */}
      <div className="mt-4 flex items-center justify-center gap-2">
        {SCENES.map((s, i) => {
          const active = i === index;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => jump(i)}
              aria-label={`Show ${s.label} scene`}
              aria-current={active ? "true" : undefined}
              className={[
                "h-[6px] rounded-full transition-all",
                "duration-[var(--duration-base)] ease-[var(--ease-out-soft)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-2",
                active ? "w-7" : "w-[6px] hover:w-3",
              ].join(" ")}
              style={{
                backgroundColor: active
                  ? "var(--color-brand-vivid)"
                  : "var(--color-ink-16)",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SCENE 1 — Chat
// ═════════════════════════════════════════════════════════════════════

const CHAT_REPLY =
  "Mediclaim with cashless network in 5,000+ hospitals. ₹10L cover. Pre-existing covered after 2 years.";

function ChatScene() {
  const reduced = useReducedMotion();
  // v3.3 fix: start with the reply bubble visible from frame 1, no phase
  // state machine. The typing dots used to gate the bottom bubble for 350ms
  // which made the scene look half-blank during scene transitions. Now the
  // reply bubble is always rendered; we just stream the characters into it.
  // With reduced motion, jump to full text instantly.
  const [chars, setChars] = useState(reduced ? CHAT_REPLY.length : 0);

  // Character-by-character stream — starts immediately on mount.
  useEffect(() => {
    if (reduced) return;
    if (chars >= CHAT_REPLY.length) return;
    const id = setTimeout(
      () => setChars((c) => Math.min(c + 2, CHAT_REPLY.length)),
      20,
    );
    return () => clearTimeout(id);
  }, [chars, reduced]);

  return (
    <div className="flex flex-col gap-2.5">
      {/* User bubble — right-aligned, ink. Always visible from frame 1. */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="self-end max-w-[78%] rounded-[14px] rounded-tr-[4px] px-3.5 py-2.5"
        style={{ backgroundColor: "var(--color-ink)", color: "var(--color-paper)" }}
      >
        <p className="text-[12.5px] leading-[1.4]">
          Which health plan is best for my parents?
        </p>
      </motion.div>

      {/* Reply bubble — always rendered. Streams characters in via setChars.
          No more empty-state window. */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="self-start max-w-[88%] rounded-[14px] rounded-tl-[4px] px-3.5 py-2.5"
        style={{ backgroundColor: "var(--color-brand-soft)" }}
      >
        {/* If we have no streamed chars yet, show the typing dots so the
            bubble has visible content from frame 1; once a char streams in,
            switch to the streaming text. */}
        {chars === 0 && !reduced ? (
          <span className="inline-flex items-center gap-1 py-1">
            <Dot delay={0} />
            <Dot delay={0.15} />
            <Dot delay={0.3} />
          </span>
        ) : (
          <p className="text-[12.5px] leading-[1.45] text-[var(--color-brand-deep)]">
            {CHAT_REPLY.slice(0, chars)}
            {chars < CHAT_REPLY.length && !reduced ? (
              <span className="opacity-60">▍</span>
            ) : null}
          </p>
        )}
      </motion.div>
    </div>
  );
}

function Dot({ delay = 0 }: { delay?: number }) {
  return (
    <motion.span
      aria-hidden="true"
      animate={{ y: [0, -3, 0] }}
      transition={{ duration: 0.9, repeat: Infinity, delay, ease: "easeInOut" }}
      className="inline-block h-1.5 w-1.5 rounded-full"
      style={{ backgroundColor: "var(--color-brand-deep)" }}
    />
  );
}

// ═════════════════════════════════════════════════════════════════════
// SCENE 2 — OCR
// ═════════════════════════════════════════════════════════════════════

const OCR_FIELDS = [
  { label: "Policy No",  value: "PH-2024-88412" },
  { label: "Sum Insured", value: "₹10,00,000" },
  { label: "Premium",    value: "₹14,200 / yr" },
  { label: "Expiry",     value: "12 Oct 2026" },
];

function OcrScene() {
  const reduced = useReducedMotion();
  // v3.3 fix: start with one field already revealed so the right-side fields
  // column has visible content from frame 1. Previously revealed=0 meant the
  // right side was completely blank for 900ms after each scene change.
  const [revealed, setRevealed] = useState(reduced ? OCR_FIELDS.length : 1);

  useEffect(() => {
    if (reduced) return;
    // Stagger the remaining fields in — first field is already at i=0.
    const ids = OCR_FIELDS.slice(1).map((_, i) =>
      setTimeout(() => setRevealed((r) => Math.max(r, i + 2)), 300 + i * 280),
    );
    return () => ids.forEach(clearTimeout);
  }, [reduced]);

  return (
    <div className="flex gap-3.5">
      {/* Faux policy document with scan-line sweep */}
      <div
        className="relative shrink-0 w-[80px] rounded-[8px] overflow-hidden"
        style={{
          aspectRatio: "0.72/1",
          backgroundColor: "var(--color-paper)",
          border: "1px solid var(--color-ink-08)",
        }}
        aria-hidden="true"
      >
        {/* Document text lines */}
        <div className="absolute inset-2 flex flex-col gap-1.5">
          <div className="h-1 w-[60%] rounded-full" style={{ backgroundColor: "var(--color-ink-16)" }} />
          <div className="h-1 w-[85%] rounded-full" style={{ backgroundColor: "var(--color-ink-08)" }} />
          <div className="h-1 w-[70%] rounded-full" style={{ backgroundColor: "var(--color-ink-08)" }} />
          <div className="h-1 w-[78%] rounded-full" style={{ backgroundColor: "var(--color-ink-08)" }} />
          <div className="h-1 w-[55%] rounded-full" style={{ backgroundColor: "var(--color-ink-08)" }} />
          <div className="h-1 w-[80%] rounded-full" style={{ backgroundColor: "var(--color-ink-08)" }} />
        </div>
        {/* Scan-line sweep */}
        {!reduced ? (
          <motion.div
            initial={{ y: "-50%" }}
            animate={{ y: "150%" }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
            className="absolute inset-x-0 h-[18px] pointer-events-none"
            style={{
              background:
                "linear-gradient(to bottom, transparent 0%, rgb(31 216 118 / 0.6) 50%, transparent 100%)",
            }}
          />
        ) : null}
      </div>

      {/* Extracted fields list */}
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        {OCR_FIELDS.map((f, i) => (
          <motion.div
            key={f.label}
            initial={false}
            animate={
              i < revealed
                ? { opacity: 1, x: 0 }
                : { opacity: 0, x: 8 }
            }
            transition={{ duration: 0.3, ease: EASE_OUT_SOFT }}
            className="flex items-baseline justify-between gap-2"
          >
            <span className="bsure-caps text-[9.5px] text-[var(--color-ink-60)]">
              {f.label}
            </span>
            <span className="text-[12.5px] font-semibold text-[var(--color-ink)] truncate">
              {f.value}
            </span>
          </motion.div>
        ))}
        <motion.div
          initial={false}
          animate={
            revealed >= OCR_FIELDS.length
              ? { opacity: 1, y: 0 }
              : { opacity: 0, y: 4 }
          }
          transition={{ duration: 0.3, delay: 0.1 }}
          className="mt-1.5 flex items-center gap-1.5"
        >
          <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
            <path
              d="M3 8.5l3 3 7-7"
              stroke="var(--color-brand-vivid)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-[11px] text-[var(--color-brand-deep)] font-semibold">
            Understood in 3s
          </span>
        </motion.div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SCENE 3 — Compare (kept from v3, in-glass)
// ═════════════════════════════════════════════════════════════════════

const INSURERS = [
  { name: "HDFC ERGO",     savings: 86 },
  { name: "ICICI Lombard", savings: 72 },
  { name: "Bajaj Allianz", savings: 64 },
  { name: "TATA AIG",      savings: 58 },
  { name: "Star Health",   savings: 48 },
  { name: "Niva Bupa",     savings: 40 },
];

function CompareScene() {
  return (
    <div className="flex flex-col gap-1.5">
      {INSURERS.map((ins, i) => (
        <motion.div
          key={ins.name}
          initial={{ opacity: 0, x: -6 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.05 + i * 0.05, duration: 0.3, ease: EASE_OUT_SOFT }}
          className="flex items-center gap-2.5"
        >
          <span className="w-[78px] shrink-0 text-[11px] text-[var(--color-ink-80)] font-medium">
            {ins.name}
          </span>
          <span
            aria-hidden="true"
            className="relative flex-1 h-[5px] rounded-[var(--radius-pill)] overflow-hidden"
            style={{ backgroundColor: "var(--color-ink-08)" }}
          >
            <motion.span
              initial={{ width: 0 }}
              animate={{ width: `${ins.savings}%` }}
              transition={{ delay: 0.15 + i * 0.05, duration: 0.55, ease: EASE_OUT_SOFT }}
              className="absolute inset-y-0 left-0 rounded-[var(--radius-pill)]"
              style={{
                background:
                  "linear-gradient(90deg, var(--color-brand-deep) 0%, var(--color-brand) 60%, var(--color-brand-vivid) 100%)",
              }}
            />
          </span>
        </motion.div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SCENE 4 — Truth audit
// ═════════════════════════════════════════════════════════════════════

const AUDIT_ROWS: { label: string; status: "ok" | "warn"; note?: string }[] = [
  { label: "Cashless network",   status: "ok",   note: "5,000+ hospitals" },
  { label: "Waiting period",     status: "warn", note: "2 years (PED)" },
  { label: "Claim settlement",   status: "ok",   note: "98.2% (FY24)" },
  { label: "Room rent capping",  status: "warn", note: "1% of sum insured" },
  { label: "No-claim bonus",     status: "ok",   note: "Up to 50%" },
];

function AuditScene() {
  return (
    <div className="flex flex-col gap-1.5">
      {AUDIT_ROWS.map((row, i) => (
        <motion.div
          key={row.label}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 + i * 0.07, duration: 0.3 }}
          className="flex items-center gap-2.5"
        >
          <span
            aria-hidden="true"
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
            style={{
              backgroundColor:
                row.status === "ok"
                  ? "var(--color-brand-soft)"
                  : "rgb(255 211 0 / 0.18)",
            }}
          >
            {row.status === "ok" ? (
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 8.5l3 3 7-7"
                  stroke="var(--color-brand-deep)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 4v5M8 11.5v0.5"
                  stroke="#8a6b00"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </span>
          <span className="text-[12px] text-[var(--color-ink)] flex-1 truncate">
            {row.label}
          </span>
          <span className="text-[11px] text-[var(--color-ink-60)] shrink-0">
            {row.note}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SCENE 5 — Renewal
// ═════════════════════════════════════════════════════════════════════

function RenewalScene() {
  return (
    <div className="flex flex-col gap-2">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: EASE_OUT_SOFT }}
        className="rounded-[14px] p-3.5"
        style={{ backgroundColor: "var(--color-brand-soft)" }}
      >
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="bsure-pulse inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: "var(--color-brand-deep)" }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 4v4l3 2"
                stroke="var(--color-paper)"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="8" cy="8" r="6" stroke="var(--color-paper)" strokeWidth="1.8" />
            </svg>
          </span>
          <div className="flex flex-col">
            <span className="bsure-caps text-[9.5px] text-[var(--color-brand-deep)]">
              Renewal due
            </span>
            <span className="text-[13.5px] font-semibold text-[var(--color-ink)] mt-0.5">
              TATA AIG renews in 12 days
            </span>
            <span className="text-[11.5px] text-[var(--color-ink-60)] mt-0.5">
              ₹14,200 · auto-pay scheduled
            </span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.35 }}
        className="rounded-[12px] px-3 py-2.5 flex items-center justify-between"
        style={{
          backgroundColor: "var(--color-paper)",
          border: "1px solid var(--color-ink-08)",
        }}
      >
        <div className="flex flex-col">
          <span className="bsure-caps text-[9.5px] text-[var(--color-ink-60)]">
            Niva Bupa
          </span>
          <span className="text-[12px] text-[var(--color-ink)] mt-0.5">
            renews in 48 days · ₹9,800
          </span>
        </div>
        <span className="text-[10.5px] text-[var(--color-brand-deep)] font-semibold">
          on track
        </span>
      </motion.div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SCENE 6 — Best suggestion
// ═════════════════════════════════════════════════════════════════════

function BestScene() {
  return (
    <div className="flex flex-col gap-2.5">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE_OUT_SOFT }}
        className="rounded-[14px] p-3.5"
        style={{
          background:
            "linear-gradient(135deg, var(--color-brand-deep) 0%, var(--color-canvas) 100%)",
          color: "var(--color-paper)",
        }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col">
            <span className="bsure-caps text-[9.5px] opacity-70">
              Best for you
            </span>
            <span className="bsure-display text-[18px] leading-[1.1] mt-1">
              HDFC Optima Restore
            </span>
            <span className="text-[12px] mt-1 opacity-85">
              ₹11,400 / yr · ₹10L cover · 5★ rated
            </span>
          </div>
          <span
            className="inline-flex h-7 px-2.5 items-center rounded-full bsure-caps text-[9.5px]"
            style={{
              backgroundColor: "var(--color-brand-vivid)",
              color: "var(--color-canvas)",
            }}
          >
            #1
          </span>
        </div>

        <div className="mt-3 pt-3 border-t border-[var(--color-paper-24)]">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11.5px] opacity-85">
              Why this · cashless, no co-pay, low waiting
            </span>
            <span aria-hidden="true">→</span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.35 }}
        className="flex items-center justify-between"
      >
        <span className="text-[11px] text-[var(--color-ink-60)]">
          You save vs current
        </span>
        <span className="text-[13px] font-semibold text-[var(--color-brand-deep)]">
          ₹4,800 / yr
        </span>
      </motion.div>
    </div>
  );
}

export default PhoneShowcase;
